/**
 * Dockable-panel layout store (shared by the Flow and SR editors).
 *
 * Tracks, per editor, where each sidebar panel lives (left/right sidebar), its
 * order within that sidebar, and whether it's collapsed to just its title.
 * Persisted to localStorage so the layout survives reloads. Panels self-register
 * on first render with their default placement; the store only overrides that
 * default once the user has moved/collapsed them.
 */

import { useMemo } from 'react';
import { create } from 'zustand';

export type DockSide = 'left' | 'right';
export type EditorKey = 'flow' | 'sr';

export interface PanelState {
  sidebar: DockSide;
  order: number;
  collapsed: boolean;
}

type EditorLayout = Record<string, PanelState>;

interface PanelLayoutStore {
  /** editorKey -> (panelId -> state) */
  layouts: Record<string, EditorLayout>;
  /** Idempotently seed a panel's default placement (no-op if already known). */
  register: (editor: EditorKey, id: string, defaultSidebar: DockSide, defaultOrder: number) => void;
  toggleCollapse: (editor: EditorKey, id: string) => void;
  /** Move `id` into `toSidebar` at `toIndex` (renumbers orders in both sidebars). */
  movePanel: (editor: EditorKey, id: string, toSidebar: DockSide, toIndex: number) => void;
  get: (editor: EditorKey, id: string) => PanelState | undefined;
}

const STORAGE_KEY = 'synapse:panel-layout';

/**
 * Canonical default distribution per editor — the "saved" layout new sessions
 * start from. Sidebar + order for each panel; panels not listed fall back to the
 * defaults passed at registration. Editing this is how you change the shipped
 * default arrangement for everyone (existing users keep their saved layout).
 */
const DEFAULT_LAYOUTS: Partial<Record<EditorKey, Record<string, { sidebar: DockSide; order: number }>>> = {
  // RIGHT column canon (reviewer-set, ALL editors): Difficulty → Notes →
  // Minimap → Visualizer, top to bottom. Editor-specific panels follow after.
  flow: {
    // Left column (top → bottom); Map is pinned to the bottom via pinnedOrder.
    'Controls':    { sidebar: 'left',  order: 0 },
    'Mixer':       { sidebar: 'left',  order: 1 },
    'Camera View': { sidebar: 'left',  order: 2 },
    'Transport':   { sidebar: 'left',  order: 3 },
    // Right column (top → bottom).
    'Difficulty':  { sidebar: 'right', order: 0 },
    'Notes':       { sidebar: 'right', order: 1 },
    'Minimap':     { sidebar: 'right', order: 2 },
    'Visualizer':  { sidebar: 'right', order: 3 },
  },
  sr: {
    'Difficulty':  { sidebar: 'right', order: 0 },
    'Notes':       { sidebar: 'right', order: 1 },
    'Minimap':     { sidebar: 'right', order: 2 },
    'Visualizer':  { sidebar: 'right', order: 3 },
    'Obstacles':   { sidebar: 'right', order: 4 },
    'Selection':   { sidebar: 'right', order: 5 },
    'Others':      { sidebar: 'right', order: 6 },
  },
};

/** Bump when DEFAULT_LAYOUTS changes and the new arrangement should be FORCED
 *  onto existing installs once (collapse state is preserved; panels the
 *  defaults don't know about keep their slot after the canonical ones). */
const LAYOUT_VERSION = 2;
const LAYOUT_VERSION_KEY = 'synapse:panel-layout:v';

function migrateLayouts(layouts: Record<string, EditorLayout>): Record<string, EditorLayout> {
  try {
    if (localStorage.getItem(LAYOUT_VERSION_KEY) === String(LAYOUT_VERSION)) return layouts;
  } catch { /* ignore */ }
  const next: Record<string, EditorLayout> = {};
  for (const [editor, layout] of Object.entries(layouts)) {
    const defaults = DEFAULT_LAYOUTS[editor as EditorKey];
    if (!defaults) { next[editor] = layout; continue; }
    const migrated: EditorLayout = {};
    for (const [id, p] of Object.entries(layout)) {
      const d = defaults[id];
      migrated[id] = d
        ? { sidebar: d.sidebar, order: d.order, collapsed: p.collapsed }
        : { ...p, order: p.order + 100 };   // unknown panels sink below the canon
    }
    next[editor] = migrated;
  }
  try { localStorage.setItem(LAYOUT_VERSION_KEY, String(LAYOUT_VERSION)); } catch { /* ignore */ }
  persist(next);
  return next;
}

function loadLayouts(): Record<string, EditorLayout> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Record<string, EditorLayout>;
  } catch { /* ignore */ }
  return {};
}

function persist(layouts: Record<string, EditorLayout>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(layouts)); } catch { /* quota */ }
}

/** Return panel ids of one sidebar, sorted by order. */
function idsInSidebar(layout: EditorLayout, sidebar: DockSide): string[] {
  return Object.entries(layout)
    .filter(([, p]) => p.sidebar === sidebar)
    .sort((a, b) => a[1].order - b[1].order)
    .map(([id]) => id);
}

export const usePanelLayout = create<PanelLayoutStore>((set, get) => ({
  layouts: migrateLayouts(loadLayouts()),

  register: (editor, id, defaultSidebar, defaultOrder) => {
    const layout = get().layouts[editor] || {};
    if (layout[id]) return; // already placed — respect the user's saved layout
    // Seed from the canonical default distribution when present, so new sessions
    // get the shipped arrangement; otherwise use the placement passed by the panel.
    const preset = DEFAULT_LAYOUTS[editor]?.[id];
    const sidebar = preset?.sidebar ?? defaultSidebar;
    const order = preset?.order ?? defaultOrder;
    set(s => {
      const next = { ...s.layouts, [editor]: { ...layout, [id]: { sidebar, order, collapsed: false } } };
      persist(next);
      return { layouts: next };
    });
  },

  toggleCollapse: (editor, id) => {
    set(s => {
      const layout = s.layouts[editor] || {};
      const cur = layout[id];
      if (!cur) return s;
      const next = { ...s.layouts, [editor]: { ...layout, [id]: { ...cur, collapsed: !cur.collapsed } } };
      persist(next);
      return { layouts: next };
    });
  },

  movePanel: (editor, id, toSidebar, toIndex) => {
    set(s => {
      const layout = { ...(s.layouts[editor] || {}) };
      if (!layout[id]) return s;

      // Build the target sidebar's ordered id list without the moved panel,
      // then splice it in at toIndex.
      const target = idsInSidebar(layout, toSidebar).filter(p => p !== id);
      const clampedIndex = Math.max(0, Math.min(toIndex, target.length));
      target.splice(clampedIndex, 0, id);

      // Reassign orders for the target sidebar (and the source, if different).
      target.forEach((pid, i) => { layout[pid] = { ...layout[pid], sidebar: toSidebar, order: i }; });
      const fromSidebar: DockSide = toSidebar === 'left' ? 'right' : 'left';
      idsInSidebar(layout, fromSidebar).forEach((pid, i) => { layout[pid] = { ...layout[pid], order: i }; });

      const next = { ...s.layouts, [editor]: layout };
      persist(next);
      return { layouts: next };
    });
  },

  get: (editor, id) => (get().layouts[editor] || {})[id],
}));

/** Hook: ordered panel ids currently assigned to a sidebar (reactive).
 *
 * Selects the (stable) layout object and derives the id list via useMemo —
 * computing a fresh array inside the zustand selector would return a new
 * reference every render and trigger an infinite update loop (React #185). */
export function useSidebarOrder(editor: EditorKey, sidebar: DockSide): string[] {
  const layout = usePanelLayout(s => s.layouts[editor]);
  return useMemo(() => idsInSidebar(layout || {}, sidebar), [layout, sidebar]);
}
